import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  getJobTitleLabel,
  getChargeOutRateTitle,
  getCostOfSupplyTitle,
} from "../../../utils/jobTitleLabel";
import { updateOrganizationCustomLabels } from "../../../../services/AuthService";

const UpdateJobTitleModal = ({ isOpen, onClose }) => {
  const [currentValues, setCurrentValues] = useState({
    jobTitleLabel: "",
    chargeOutRateTitle: "",
    costOfSupplyTitle: "",
  });
  const [newValues, setNewValues] = useState({
    jobTitleLabel: "",
    chargeOutRateTitle: "",
    costOfSupplyTitle: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const InfoTooltip = ({ text }) => (
    <span className="relative inline-flex group ml-1 align-middle">
      <span
        className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold text-[#166473] bg-[#166473]/10 cursor-help ring-1 ring-inset ring-[#166473]/15 group-hover:bg-[#166473]/15 transition-colors"
        aria-label="Info"
      >
        i
      </span>
      <span
        className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 w-[280px] -translate-x-1/2 rounded-lg bg-slate-900 px-3 py-2 text-xs leading-5 text-white opacity-0 shadow-xl ring-1 ring-black/10 transition-all duration-150 group-hover:opacity-100 group-hover:translate-y-0"
      >
        {text}
        <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 h-2 w-2 rotate-45 bg-slate-900" />
      </span>
    </span>
  );

  useEffect(() => {
    if (isOpen) {
      const next = {
        jobTitleLabel: getJobTitleLabel(),
        chargeOutRateTitle: getChargeOutRateTitle(),
        costOfSupplyTitle: getCostOfSupplyTitle(),
      };
      setCurrentValues(next);
      setNewValues(next);
      setError("");
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      jobTitleLabel: (newValues.jobTitleLabel || "").trim(),
      chargeOutRateTitle: (newValues.chargeOutRateTitle || "").trim(),
      costOfSupplyTitle: (newValues.costOfSupplyTitle || "").trim(),
    };

    if (!payload.jobTitleLabel || !payload.chargeOutRateTitle || !payload.costOfSupplyTitle) {
      setError("Please enter all labels.");
      return;
    }
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const orgId = userDetails?.user?.organizationId;
    if (!orgId) {
      toast.error("Organization not found.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await updateOrganizationCustomLabels(orgId, payload);
      toast.success("Default labels updated successfully.");
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to update.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white p-8 rounded-2xl shadow-xl mx-auto w-full max-w-md relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
          onClick={onClose}
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>

        <h3 className="text-xl font-semibold text-primary-headings mb-2">Update Default Labels</h3>
        <p className="text-sm text-primary-light mb-6">
          Configure your organization-wide default labels. These labels are used across lists, forms, summaries and details screens.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-accent-dark mb-1">Current job title label</label>
              <div className="px-4 py-2.5 bg-accent-light/10 border border-accent-light/30 rounded-lg text-accent-dark">
                {currentValues.jobTitleLabel || "Job"}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-accent-dark mb-1">
                New label <span className="text-red-500">*</span>
                <InfoTooltip text='Used for base job naming across sidebar/menu, dashboards and all job pages.' />
              </label>
              <input
                type="text"
                className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-light focus:border-transparent"
                value={newValues.jobTitleLabel}
                onChange={(e) => setNewValues((prev) => ({ ...prev, jobTitleLabel: e.target.value }))}
                placeholder="e.g. Booking"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-dark mb-1">
                Charge Out Rate title <span className="text-red-500">*</span>
                <InfoTooltip text='Used where customer-facing charges are shown, e.g. task list, task details, job list and total charge sections.' />
              </label>
              <input
                type="text"
                className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-light focus:border-transparent"
                value={newValues.chargeOutRateTitle}
                onChange={(e) => setNewValues((prev) => ({ ...prev, chargeOutRateTitle: e.target.value }))}
                placeholder="e.g. Price Charged"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-dark mb-1">
                Cost of Supply title <span className="text-red-500">*</span>
                <InfoTooltip text='Used for internal supply/operational cost shown in Create Job and Job Cost Summary.' />
              </label>
              <input
                type="text"
                className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-light focus:border-transparent"
                value={newValues.costOfSupplyTitle}
                onChange={(e) => setNewValues((prev) => ({ ...prev, costOfSupplyTitle: e.target.value }))}
                placeholder="e.g. Internal Cost"
              />
              {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-accent-light/20 flex justify-end gap-3">
            <button
              type="button"
              className="px-5 py-2.5 border border-accent-light/30 rounded-lg text-accent-dark hover:bg-accent-light/5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-light transition-colors duration-200"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 bg-[#166473] text-white rounded-lg hover:bg-[#166473]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#166473] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              disabled={loading}
            >
              {loading ? (
                <span className="inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                "Update"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateJobTitleModal;
